/**
 * UniversalCanvasMediaBrowserModalController is a class that manages the media browser modal for Universal Canvas.
 * It handles the initialization, palette management, and interaction with the modal.
 */
export default class UniversalCanvasMediaBrowserModalController {
    /**
     * Constructor for UniversalCanvasMediaBrowserModalController.
     * @param {Object} $scope - Angular scope object.
     * @param {Object} $uibModalInstance - UI Bootstrap modal instance.
     * @param {Object} $timeout - Angular timeout service.
     * @param {Object} $interval - Angular interval service.
     * @param {Object} taskContext - Task context object.
     * @param {Object} $bridges - Bridges service.
     * @param {Object} APIService - API service object.
     * @param {string} contentObjectUUID - UUID of the content object.
     * @param {string} currentmediaUUID - UUID of the current media.
     * @param {string} taskuuid - UUID of the task.
     * @param {Array} mediaList - List of media objects.
     * @param {string} defaultPaletteName - Default palette name.
     * @param {Object} settings - Settings object.
     */
    constructor($scope, $uibModalInstance, $timeout, $interval, taskContext, $bridges, APIService, contentObjectUUID, currentmediaUUID, taskuuid, mediaList, defaultPaletteName, settings) {
        this.$scope = $scope;
        this.$uibModalInstance = $uibModalInstance;
        this.$timeout = $timeout;
        this.$interval = $interval;
        this.taskContext = taskContext;
        this.$bridges = $bridges;
        this.APIService = APIService;
        this.contentObjectUUID = contentObjectUUID;
        this.currentmediaUUID = currentmediaUUID;
        this.taskuuid = taskuuid;
        this.mediaList = mediaList;
        this.defaultPaletteName = defaultPaletteName;
        this.settings = settings;

        this.$logger = $bridges.logger();
        this.objectsOnDisk = null;
        this.backgroundDefaultImagePalettePrefix = '__IB-BACKGROUND-IMAGE-DEFAULT__';
        this.backgroundImagesPalettePrefix = '__IB-BACKGROUND-IMAGES__';
        this.paletteList = null;
        this.paletteHasChanged = false;

        this.$scope.showonly = 'image';
        this.$scope.contentobjectuuid = contentObjectUUID;
        this.$scope.currentmediauuid = currentmediaUUID;
        this.$scope.backgroundDefaultImagePaletteName = this.backgroundDefaultImagePalettePrefix + contentObjectUUID;
        this.$scope.backgroundImagesPaletteName = this.backgroundImagesPalettePrefix + contentObjectUUID;

        this.init();
    }

    /**
     * Initializes the modal by setting up the palettes and media lists.
     */
    init() {
        this.paletteList = this.getPaletteList();
        this.objectsOnDisk = this.getEntityList();

        this.$scope.modal = {};
        this.$scope.modal.mediaFilterType = this.$scope.showonly;
        this.$scope.modal.mediaList = this.mediaList;
        this.$scope.modal.mediaObjectList = [];
        this.$scope.modal.mediaFilterType = 'image';
        this.$scope.modal.palettes = {};
        this.$scope.modal.palettescopy = {};
        this.$scope.modal.currentPaletteName = '';
        this.$scope.modal.currentPalette = '';
        this.$scope.modal.listOfUsedItems = {};
        this.$scope.modal.onCurrentPaletteNameError = false;

        this.$scope.modal.palettes = this.paletteList;
        this.$scope.modal.palettescopy = angular.copy(this.$scope.modal.palettes);

        if (this.settings && this.settings.backgroundPalette) {
            this.$scope.modal.sortableOptions = {
                'backgroundDefaultImage': {
                    'cursor': 'move',
                    stop: (evt, ui) => {
                        for (let index in this.$scope.modal[this.$scope.backgroundDefaultImagePaletteName]) {
                            this.$scope.modal[this.$scope.backgroundDefaultImagePaletteName][index].position = index;
                        }
                    }
                },
                'backgroundImages': {
                    'cursor': 'move',
                    stop: (evt, ui) => {
                        for (let index in this.$scope.modal[this.$scope.backgroundImagesPaletteName]) {
                            this.$scope.modal[this.$scope.backgroundImagesPaletteName][index].position = index;
                        }
                        let currentPalette = _.find(this.$scope.modal.palettescopy, item => item.name === this.$scope.backgroundImagesPaletteName);
                        this.$scope.modal.palettescopy[currentPalette.uuid].items = this.$scope.modal[this.$scope.backgroundImagesPaletteName].map(item => ({
                            "position": item.position,
                            "uuid": item.uuid
                        }));
                    }
                }
            };

            this.$scope.modal[this.$scope.backgroundDefaultImagePaletteName] = [];
            this.$scope.modal[this.$scope.backgroundImagesPaletteName] = [];

            this.$timeout(() => {
                let currentPalette = _.find(this.$scope.modal.palettescopy, item => item.name === this.$scope.backgroundDefaultImagePaletteName);
                this.$scope.modal[this.$scope.backgroundDefaultImagePaletteName] = this.getPaletteMediaObjectListFromMediaList(this.$scope.modal.palettescopy[currentPalette.uuid].items);
                this.$scope.modal[this.$scope.backgroundDefaultImagePaletteName].sort(this.compare);
            }, 1);

            this.$timeout(() => {
                let currentPalette = _.find(this.$scope.modal.palettescopy, item => item.name === this.$scope.backgroundImagesPaletteName);
                this.$scope.modal[this.$scope.backgroundImagesPaletteName] = this.getPaletteMediaObjectListFromMediaList(this.$scope.modal.palettescopy[currentPalette.uuid].items);
                this.$scope.modal[this.$scope.backgroundImagesPaletteName].sort(this.compare);
            }, 1);

            this.$timeout(() => {
                let defaultBackgroundImage = this.$scope.modal[this.$scope.backgroundDefaultImagePaletteName].reduce((obj, key) => {
                    obj[key.uuid] = key.uuid;
                    return obj;
                }, {});
                let backgroundImages = this.$scope.modal[this.$scope.backgroundImagesPaletteName].reduce((obj, key) => {
                    obj[key.uuid] = key.uuid;
                    return obj;
                }, {});
                this.$scope.modal.listOfUsedItems = angular.extend(defaultBackgroundImage, backgroundImages);
            }, 1);
        } else {
            this.$scope.modal.sortableOptions = {
                'cursor': 'move',
                stop: (evt, ui) => {
                    for (let index in this.$scope.modal.mediaObjectList) {
                        this.$scope.modal.mediaObjectList[index].position = index;
                    }
                    this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items = this.$scope.modal.mediaObjectList.map(item => ({
                        "position": item.position,
                        "uuid": item.uuid
                    }));
                },
            };

            if (Object.keys(this.$scope.modal.palettes).length == 1) {
                this.$timeout(() => {
                    this.$scope.modal.currentPalette = Object.keys(this.$scope.modal.palettescopy)[0];
                    this.$scope.modal.mediaObjectList = this.getPaletteMediaObjectListFromMediaList(this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items);
                    this.$scope.modal.mediaObjectList.sort(this.compare);
                    this.$scope.modal.listOfUsedItems = this.$scope.modal.mediaObjectList.reduce((obj, key) => {
                        obj[key.uuid] = key.uuid;
                        return obj;
                    }, {});
                }, 1);
            }
        }
    }

    /**
     * Creates an empty palette structure.
     * @param {string} name - Name of the palette.
     * @returns {Object} - Empty palette structure.
     */
    emptyPaletteStructure(name) {
        return {
            uuid: angular.createUUID(),
            type: 'universalCanvasPalette',
            name: name ? name : this.defaultPaletteName,
            items: [],
        };
    }

    /**
     * Retrieves the list of palettes.
     * @returns {Object} - List of palettes.
     */
    getPaletteList() {
        let universalCanvasPaletteObjects = this.taskContext.findInstancesByType('universalCanvasPalette');
        let instances = {};
        let existsImageDefaultPalette = false;
        let existsImagesPalette = false;

        for (let key in universalCanvasPaletteObjects) {
            if (universalCanvasPaletteObjects.hasOwnProperty(key)) {
                let universalCanvasPaletteObject = universalCanvasPaletteObjects[key];

                if (this.settings && this.settings.backgroundPalette) {
                    if (universalCanvasPaletteObject.getName() === this.$scope.backgroundDefaultImagePaletteName) {
                        existsImageDefaultPalette = true;
                    } else if (universalCanvasPaletteObject.getName() === this.$scope.backgroundImagesPaletteName) {
                        existsImagesPalette = true;
                    } else {
                        continue;
                    }
                } else if (universalCanvasPaletteObject.getName().indexOf(this.backgroundDefaultImagePalettePrefix) !== -1 || universalCanvasPaletteObject.getName().indexOf(this.backgroundImagesPalettePrefix) !== -1) {
                    continue;
                }
                instances[key] = {
                    uuid: key,
                    type: universalCanvasPaletteObject.getType(),
                    name: universalCanvasPaletteObject.getName(),
                    items: universalCanvasPaletteObject.getItems(),
                }
            }
        }

        if (this.settings && this.settings.backgroundPalette) {
            if (!existsImageDefaultPalette) {
                let palette = this.emptyPaletteStructure(this.$scope.backgroundDefaultImagePaletteName);
                instances[palette.uuid] = palette;
            }
            if (!existsImagesPalette) {
                let palette = this.emptyPaletteStructure(this.$scope.backgroundImagesPaletteName);
                instances[palette.uuid] = palette;
            }
        }

        return instances;
    }

    /**
     * Retrieves the list of entities from the API.
     * @returns {Array} - List of entities.
     */
    getEntityList() {
        let result = [];

        this.APIService.getEntityList(this.taskuuid)
            .then(response => {
                result.push(response.data.objects);
            }, response => {
                this.$logger.error(this.$scope._logIdentifier, 'Cannot read Task %s\nresponse: %s', this.taskuuid, response);
            });
        return result;
    }

    /**
     * Checks if an object is on disk.
     * @param {string} objectUUID - UUID of the object.
     * @returns {boolean} - True if the object is on disk, false otherwise.
     */
    isOnDisk(objectUUID) {
        return this.objectsOnDisk[0].indexOf(objectUUID) != -1 ? true : false;
    }

    /**
     * Retrieves a media object by its UUID.
     * @param {string} wantedUUID - UUID of the media object.
     * @returns {Object} - Media object.
     */
    getMediaObject(wantedUUID) {
        let _result;

        for (let key in this.$scope.modal.mediaList) {
            if (wantedUUID == this.$scope.modal.mediaList[key].uuid) {
                _result = this.$scope.modal.mediaList[key];
                break;
            }
        }
        return _result;
    }

    /**
     * Retrieves the list of media objects from the palette.
     * @param {Array} arrMediaInPalette - Array of media objects in the palette.
     * @returns {Array} - List of media objects.
     */
    getPaletteMediaObjectListFromMediaList(arrMediaInPalette) {
        let _res = [];
        angular.forEach(arrMediaInPalette, (data, key) => {
            if (data.uuid) {
                let media = this.getMediaObject(data.uuid);
                if (media) {
                    _res.push({
                        uuid: data.uuid,
                        position: data.position,
                        type: media.type,
                        thumbnail: media.thumbnail,
                        originalname: media.originalname
                    });
                }
            }
        }, _res);
        return _res;
    }

    /**
     * Compares two objects by their position.
     * @param {Object} a - First object.
     * @param {Object} b - Second object.
     * @returns {number} - Comparison result.
     */
    compare(a, b) {
        if (a.position < b.position) {
            return -1;
        }
        if (a.position > b.position) {
            return 1;
        }
        return 0;
    }

    /**
     * Creates a new palette.
     * @returns {Object} - New palette.
     */
    newPalette() {
        let palette = this.emptyPaletteStructure();
        this.$scope.modal.palettescopy[palette.uuid] = palette;
        return palette;
    }

    /**
     * Handles the selection of a palette.
     */
    onSelectPalette() {
        if (angular.isDefined(this.$scope.modal.currentPalette)) {
            this.$scope.modal.currentPaletteName = this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].name;
            this.$scope.modal.mediaObjectList = this.getPaletteMediaObjectListFromMediaList(this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items);
            this.$scope.modal.mediaObjectList.sort(this.compare);
            this.$scope.modal.listOfUsedItems = this.$scope.modal.mediaObjectList.reduce((obj, key) => {
                obj[key.uuid] = key.uuid;
                return obj;
            }, {});
        }
    }

    /**
     * Callback for the drag start event in the Universal Canvas.
     * @param {Event} evnt - Event object.
     * @param {Object} ui - UI object.
     */
    universalCanvasOnDragStartCallback(evnt, ui) {
        // var source = ui.helper.context;
        // console.log('onDragStart id', evnt.currentTarget.id);
    }

    /**
     * Callback for the drop event in the Universal Canvas.
     * @param {Event} evnt - Event object.
     * @param {Object} ui - UI object.
     * @param {Object} data - Data object.
     * @returns {boolean} - False if the operation is invalid, true otherwise.
     */
    universalCanvasOnDropCallback(evnt, ui, data) {
        if (typeof ui.helper.sortable === 'object') {
            return false;
        }

        if (!data) {
            data = {};
        }

        if (data && data.type) {
            let currentPalette = _.find(this.$scope.modal.palettescopy, item => item.name === data.type);
            this.$scope.modal.currentPalette = currentPalette.uuid;
            this.$scope.modal.currentPaletteName = currentPalette.name;
        } else {
            data.type = 'mediaObjectList';
        }
        if (data.type === this.$scope.backgroundDefaultImagePaletteName && this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items.length > 0) {
            delete this.$scope.modal.listOfUsedItems[this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items[0].uuid];
            this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items.splice(0, 1);
            this.$scope.modal[this.$scope.backgroundDefaultImagePaletteName].splice(0, 1);
        }

        let selectedMediaUUID = ui.helper.context.id;

        let isPalettesEmpty = Object.keys(this.$scope.modal.palettescopy).length == 0 ? true : false;

        if (isPalettesEmpty || !this.$scope.modal.currentPalette) {
            let palette = this.newPalette();

            palette.items.push({uuid: selectedMediaUUID, position: 0});

            this.$scope.modal.palettescopy[palette.uuid] = palette;

            this.$timeout(() => {
                this.$scope.modal.currentPalette = palette.uuid;
                this.$scope.modal.currentPaletteName = palette.name;
                this.$scope.modal[data.type] = this.getPaletteMediaObjectListFromMediaList(this.$scope.modal.palettescopy[palette.uuid].items);
            }, 1);

        } else {
            if (angular.isDefined(this.$scope.modal.currentPalette) && this.$scope.modal.currentPalette) {

                if (!this.$scope.modal.listOfUsedItems[selectedMediaUUID]) {
                    this.$timeout(() => {
                        let nextIndex = Object.keys(this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items).length;
                        this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items.push({uuid: selectedMediaUUID, position: nextIndex});
                        this.$scope.modal[data.type] = this.getPaletteMediaObjectListFromMediaList(this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items);
                    }, 1);
                } else {
                    //ignore
                }

            } else {
                console.error('there is no active palette!');

            }

        }

        this.$scope.modal.listOfUsedItems[selectedMediaUUID] = selectedMediaUUID;
    }

    onModalCloseButtonClick() {
        if (this.$uibModalInstance !== null) {
            this.$uibModalInstance.close(
                {
                    reloadCanvas: this.paletteHasChanged
                }
            );
        }
    }

    onPaletteModalSaveChangesButtonClick(closeOnClickParam) {
        let closeOnClick = closeOnClickParam ? closeOnClickParam : false;
        this.paletteHasChanged = true;

        if (this.settings && this.settings.backgroundPalette) {
            for (let key in this.$scope.modal.palettescopy) {
                if (this.$scope.modal.palettescopy.hasOwnProperty(key)) {
                    let paletteCopy = this.$scope.modal.palettescopy[key];
                    if (this.taskContext.exists(paletteCopy.uuid)) {
                        this.taskContext.update(paletteCopy.uuid, paletteCopy);
                    } else {
                        this.taskContext.write(paletteCopy.uuid, paletteCopy);
                    }
                }
            }
        } else if (this.$scope.modal.currentPalette) {

            if (this.defaultPaletteName != this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].name && this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].name) {
                this.$scope.modal.palettes = angular.copy(this.$scope.modal.palettescopy);

                if (Object.keys(this.paletteList).indexOf(this.$scope.modal.currentPalette) == -1) {
                    this.taskContext.write(this.$scope.modal.currentPalette, this.$scope.modal.palettes[this.$scope.modal.currentPalette]);
                } else {
                    this.taskContext.update(this.$scope.modal.currentPalette, this.$scope.modal.palettes[this.$scope.modal.currentPalette]);
                }
            } else {
                console.error('Palette does not have a name!');
                closeOnClick = false;
                this.$scope.modal.onCurrentPaletteNameError = true;
                this.$interval(() => {
                    this.$scope.modal.onCurrentPaletteNameError = !this.$scope.modal.onCurrentPaletteNameError;
                }, 500, 5);
            }

            if (this.$uibModalInstance !== null && closeOnClick) {
                this.$uibModalInstance.close(
                    {
                        reloadCanvas: this.paletteHasChanged
                    }
                );
            }
        }
    }

    onPaletteModalRemoveCardButtonClick(cardUUID, type) {
        if (angular.isDefined(cardUUID)) {
            if (type) {
                let currentPalette = _.find(this.$scope.modal.palettescopy, item => item.name === type);
                this.$scope.modal.currentPalette = currentPalette.uuid;
            } else {
                type = 'mediaObjectList'
            }

            //todo: do one remove function
            for (let idx = this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items.length - 1; idx >= 0; idx--) {
                if (cardUUID == this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items[idx].uuid) {
                    this.$scope.modal.palettescopy[this.$scope.modal.currentPalette].items.splice(idx, 1);
                }
            }

            for (let idx = this.$scope[type].length - 1; idx >= 0; idx--) {
                if (cardUUID == this.$scope[type][idx].uuid) {
                    this.$scope[type].splice(idx, 1);
                }
            }

            if (type === 'mediaObjectList') {
                /* Palettes */
                this.$scope.modal.listOfUsedItems = this.$scope[type].reduce((obj, key) => {
                    obj[key.uuid] = key.uuid;
                    return obj;
                }, {});
            } else {
                /* BAckground Image */
                let defaultBackgroundImage = this.$scope[this.backgroundDefaultImagePaletteName].reduce((obj, key) => {
                    obj[key.uuid] = key.uuid;
                    return obj;
                }, {});
                let backgroundImages = this.$scope[this.backgroundImagesPaletteName].reduce((obj, key) => {
                    obj[key.uuid] = key.uuid;
                    return obj;
                }, {});
                this.$scope.modal.listOfUsedItems = angular.extend(defaultBackgroundImage, backgroundImages);
            }
        }
    }

    onNewPaletteButtonClick() {
        let palette = this.newPalette();

        this.$timeout(() => {
            this.$scope.modal.currentPalette = palette.uuid;
            this.$scope.modal.currentPaletteName = palette.name;
            this.$scope.modal.mediaObjectList = [];
            this.$scope.modal.listOfUsedItems = {};
        }, 1);
    }

    onDeletePaletteButtonClick(paletteUUID) {
        // read current selected Palette on Dropdown and ask for a confirmation
        // console.log('onDeletePaletteButtonClick clicked!', paletteUUID, $scope.modal.palettescopy[paletteUUID].name);

        if (typeof paletteUUID != 'undefined') {
            this.paletteHasChanged = true;

            delete this.$scope.modal.palettescopy[paletteUUID];
            this.$scope.modal.palettes = angular.copy(this.$scope.modal.palettescopy);
            this.$scope.modal.mediaObjectList = [];
            this.$scope.modal.currentPalette = '';
            this.$scope.modal.currentPaletteName = '';
            this.$scope.modal.listOfUsedItems = {};

            if (this.isOnDisk(paletteUUID)) {
                this.APIService
                    .deleteEntity(
                        this.taskuuid,
                        paletteUUID
                    )
                    .then(data => {
                        this.taskContext.remove(paletteUUID);
                        this.$logger.info(this.$scope._logIdentifier, 'universal canvas palette ' + paletteUUID + ' has been removed from disk');
                    }, response => {
                        this.$logger.error(this.$scope._logIdentifier, 'delete error! Cannot delete UniversalCanvasPalette Object ' + paletteUUID + '\nresponse:', response);
                    });
            } else {
                // remove from loaded repository
                this.taskContext.flagEntityAsDeleted(paletteUUID);
                this.taskContext.remove(paletteUUID);
            }
        }
    }
}